import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatTimeID, formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { MapPin, Users, Clock, Download, Search, CalendarDays, Store, X, Camera, Pencil } from "lucide-react";
import EmpPicker from "@/components/EmpPicker";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const PINK = "#C4738A";
const MAROON = "#7C2D3E";
const STATUS_LABEL = { hadir: "Hadir", terlambat: "Terlambat", sakit: "Sakit", cuti: "Cuti", izin: "Izin", belum_absen: "Belum Absen", libur: "Libur" };
const STATUS_CLS = { hadir: "bg-emerald-50 text-emerald-600", terlambat: "bg-amber-50 text-amber-600", sakit: "bg-red-50 text-red-500", cuti: "bg-violet-50 text-violet-600", izin: "bg-blue-50 text-blue-600", belum_absen: "bg-slate-100 text-slate-400", libur: "bg-sky-50 text-sky-600" };
const DOT_CLS = { hadir: "bg-emerald-500", terlambat: "bg-amber-400", belum_absen: "bg-slate-300", libur: "bg-sky-400" };

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const CELL_CLS = { H: "bg-emerald-100 text-emerald-700", T: "bg-amber-100 text-amber-600", A: "bg-rose-100 text-rose-500", I: "bg-blue-100 text-blue-600", S: "bg-violet-100 text-violet-600", C: "bg-cyan-100 text-cyan-600", L: "bg-slate-100 text-slate-400" };
const LEGEND = [["H", "Hadir"], ["T", "Terlambat"], ["A", "Alfa"], ["I", "Izin"], ["S", "Sakit"], ["C", "Cuti"], ["L", "Libur"]];

const hhmmWIB = (iso) => { try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" }).replace(".", ":"); } catch { return ""; } };

function avatarPin(emp) {
  const color = emp.status === "hadir" ? "#10b981" : emp.status === "terlambat" ? "#f59e0b" : "#94a3b8";
  const inner = emp.photo
    ? `<img src="${emp.photo}" style="width:100%;height:100%;object-fit:cover;border-radius:9999px" />`
    : `<div style="width:100%;height:100%;border-radius:9999px;background:#e2e8f0;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#64748b">${(emp.name || "?")[0]}</div>`;
  return L.divIcon({
    className: "",
    html: `<div style="width:38px;height:38px;border-radius:9999px;border:3px solid ${color};box-shadow:0 2px 8px rgba(0,0,0,.3);overflow:hidden;background:#fff">${inner}</div>`,
    iconSize: [38, 38], iconAnchor: [19, 19],
  });
}

const storePin = (name) => L.divIcon({
  className: "",
  html: `<div style="background:${MAROON};color:#fff;font-size:10px;font-weight:700;padding:4px 9px;border-radius:9px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.3);transform:translateY(-50%)">${name}</div>`,
  iconSize: null, iconAnchor: [40, 20],
});

export default function AdminAttendance() {
  const [date, setDate] = useState(() => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("map");
  const [q, setQ] = useState("");
  const [rc, setRc] = useState({ emp: [] });
  const [mo, setMo] = useState(() => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 7));
  const [mf, setMf] = useState({ role: "", loc: "" });
  const [mx, setMx] = useState(null);
  const [sel, setSel] = useState(null);
  const [showSelfie, setShowSelfie] = useState(false);
  const [corr, setCorr] = useState(null);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const gpsRef = useRef(null);
  const gpsMap = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/attendance/monitor", { params: { date } }).then((r) => setData(r.data)).catch(() => setData(false));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const loadMonthly = useCallback(() => {
    if (tab !== "rekap") return;
    setMx(null);
    setSel(null);
    setShowSelfie(false);
    setCorr(null);
    api.get("/admin/attendance/monthly", { params: { month: mo, employee_id: rc.emp.join(","), role: mf.role, location_id: mf.loc } })
      .then((r) => setMx(r.data))
      .catch((e) => { setMx(false); toast.error(e?.response?.data?.detail || "Gagal memuat absensi bulanan"); });
  }, [tab, mo, rc, mf]);
  useEffect(() => { loadMonthly(); }, [loadMonthly]);

  useEffect(() => {
    if (!showSelfie || !sel?.d || sel.d.lat == null || sel.d.lng == null || !gpsRef.current) return;
    if (gpsMap.current) { gpsMap.current.remove(); gpsMap.current = null; }
    const map = L.map(gpsRef.current, { zoomControl: false, attributionControl: false }).setView([sel.d.lat, sel.d.lng], 16);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    L.marker([sel.d.lat, sel.d.lng], {
      icon: L.divIcon({ className: "", html: `<div style="width:14px;height:14px;border-radius:9999px;background:#C4738A;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.4)"></div>`, iconSize: [14, 14], iconAnchor: [7, 7] }),
    }).addTo(map);
    gpsMap.current = map;
    return () => { if (gpsMap.current) { gpsMap.current.remove(); gpsMap.current = null; } };
  }, [showSelfie, sel]);

  const openCorrection = () => {
    if (!sel?.d) return;
    const st = ["hadir", "terlambat", "sakit", "izin", "cuti"].includes(sel.d.status) ? sel.d.status : "hadir";
    setCorr({ status: st, clock_in: sel.d.clock_in ? hhmmWIB(sel.d.clock_in) : "", clock_out: sel.d.clock_out ? hhmmWIB(sel.d.clock_out) : "", note: sel.d.correction_note || "" });
  };

  const submitCorrection = async () => {
    try {
      await api.put("/admin/attendance/correct", {
        user_id: sel.emp.id, date: sel.d.date, status: corr.status,
        clock_in: corr.clock_in || null, clock_out: corr.clock_out || null, note: corr.note || null,
      });
      toast.success("Absensi berhasil dikoreksi");
      setCorr(null);
      loadMonthly();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan koreksi"); }
  };

  const emps = useMemo(() => (data?.employees || []).filter((e) => e.name.toLowerCase().includes(q.toLowerCase())), [data, q]);
  const located = useMemo(() => emps.filter((e) => e.lat != null && e.lng != null), [emps]);

  useEffect(() => {
    if (tab !== "map" || !data || !mapRef.current) return;
    if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; }
    const locs = data.locations || [];
    const first = located[0];
    const center = locs[0] ? [locs[0].lat, locs[0].lng] : first ? [first.lat, first.lng] : [-6.732, 108.5523];
    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false }).setView(center, 14);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    locs.forEach((l) => {
      L.circle([l.lat, l.lng], { radius: l.radius_m || 150, color: PINK, weight: 1.5, fillColor: PINK, fillOpacity: 0.07 }).addTo(map);
      L.marker([l.lat, l.lng], { icon: storePin(l.name) }).addTo(map);
    });
    located.forEach((e) => {
      L.marker([e.lat, e.lng], { icon: avatarPin(e) })
        .bindPopup(`<b>${e.name}</b><br/>${e.role_label || ""}<br/>Masuk ${e.clock_in ? new Date(e.clock_in).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "-"} • ${STATUS_LABEL[e.status]}${e.location_name ? `<br/>${e.location_name} (±${e.distance_m} m)` : ""}`)
        .addTo(map);
    });
    mapObj.current = map;
    return () => { if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; } };
  }, [tab, data, located]);

  const download = async () => {
    const isMonthly = tab === "rekap";
    try {
      const r = await api.get(isMonthly ? "/admin/attendance/monthly.xlsx" : "/admin/attendance/recap.xlsx", {
        params: isMonthly
          ? { month: mo, employee_id: rc.emp.join(","), role: mf.role, location_id: mf.loc }
          : { date_from: date, date_to: date },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = isMonthly ? `absensi-bulanan-${mo}.xlsx` : `rekap-absensi-${date}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Rekap absensi diunduh");
    } catch { toast.error("Gagal mengunduh rekap"); }
  };

  if (data === false) return <div className="py-20 text-center text-xs text-slate-400">Halaman ini khusus admin/HR.</div>;
  const s = data?.stats;

  return (
    <div data-testid="attendance-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Monitoring Karyawan</h1>
          <p className="text-xs text-slate-400">Absensi, jadwal kerja & lokasi terakhir saat absen — bukan pelacakan real-time.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <CalendarDays className="w-3.5 h-3.5 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="att-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-9 pl-8 pr-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]" />
          </div>
          <button data-testid="att-download" onClick={download} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1.5 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">
            <Download className="w-3.5 h-3.5" /> Unduh Rekap
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { k: "hadir", label: "Hadir", cls: "text-emerald-600", Icon: Users },
          { k: "terlambat", label: "Terlambat", cls: "text-amber-500", Icon: Clock },
          { k: "belum_absen", label: "Belum Absen", cls: "text-slate-500", Icon: Clock },
          { k: "libur", label: "Libur", cls: "text-sky-500", Icon: CalendarDays },
          { k: "lokasi_terbaru", label: "Lokasi Terlapor", cls: "text-[#C4738A]", Icon: MapPin },
        ].map((c) => (
          <div key={c.k} data-testid={`att-stat-${c.k}`} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center ${c.cls}`}><c.Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" /></div>
            <div><div className="text-[10px] text-slate-400">{c.label}</div>
              <div className={`font-display text-xl font-bold ${c.cls}`}>{s ? s[c.k] : "…"}</div></div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200">
        {[{ id: "map", label: "Peta Karyawan" }, { id: "rekap", label: "Rekap Absensi" }].map((t) => (
          <button key={t.id} data-testid={`att-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#C4738A] text-[#C4738A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "map" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
          <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div data-testid="att-map" ref={mapRef} className="h-[480px] w-full z-0" />
            <p className="text-[10px] text-slate-400 px-4 py-2.5">Lokasi terakhir dilaporkan saat absen — bukan posisi real-time pasti.</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
            <h3 className="font-display font-bold text-sm text-slate-900">Posisi Karyawan</h3>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="att-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari karyawan..."
                className="h-9 w-full pl-8 pr-3 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]" />
            </div>
            <div className="divide-y divide-slate-50 max-h-[420px] overflow-y-auto">
              {emps.map((e) => (
                <div key={e.id} data-testid={`att-pos-${e.id}`} className="py-2.5 flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                    {e.photo ? <img src={e.photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs font-bold text-slate-400">{(e.name || "?")[0]}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 truncate">{e.name}</div>
                    <div className="text-[10px] text-slate-400 truncate">{e.location_name || e.role_label}{e.clock_in ? ` • ${formatTimeID(e.clock_in)}` : ""}</div>
                  </div>
                  {e.status === "libur" ? <span className="text-[9px] font-bold bg-sky-50 text-sky-600 rounded-full px-2 py-0.5">LIBUR</span>
                    : e.lat != null ? (
                      <span className={`text-[9px] font-bold rounded-full px-2 py-0.5 ${e.in_area ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{e.in_area ? "Dalam Area" : "Luar Area"}</span>
                    ) : <span className="text-[9px] font-bold bg-slate-100 text-slate-400 rounded-full px-2 py-0.5">Belum Absen</span>}
                </div>
              ))}
              {!emps.length && <p className="py-8 text-center text-xs text-slate-400">Tidak ada karyawan cocok.</p>}
            </div>
          </div>
        </div>
      )}

      {tab === "rekap" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Bulan</label>
              <input data-testid="mx-month" type="month" value={mo} onChange={(e) => e.target.value && setMo(e.target.value)}
                className="h-9 w-full px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Divisi</label>
              <select data-testid="mx-role" value={mf.role} onChange={(e) => setMf((f) => ({ ...f, role: e.target.value }))}
                className="h-9 w-full px-2.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]">
                <option value="">Semua Divisi</option>
                {(mx?.options?.roles || []).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Lokasi</label>
              <select data-testid="mx-loc" value={mf.loc} onChange={(e) => setMf((f) => ({ ...f, loc: e.target.value }))}
                className="h-9 w-full px-2.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]">
                <option value="">Semua Lokasi</option>
                {(mx?.options?.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Karyawan</label>
              <EmpPicker options={mx?.options?.employees || []} value={rc.emp} onChange={(ids) => setRc((r) => ({ ...r, emp: ids }))} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { label: "Karyawan Dipilih", val: mx ? mx.employees.length : "…", Icon: Users, testid: "mx-stat-emps" },
              { label: "Periode", val: mx ? `1 – ${mx.days_in_month} ${MONTHS_ID[mx.month_num - 1]} ${mx.year}` : "…", Icon: CalendarDays, testid: "mx-stat-period" },
              { label: "Hari dalam Bulan", val: mx ? mx.days_in_month : "…", Icon: CalendarDays, testid: "mx-stat-days" },
            ].map((c) => (
              <div key={c.testid} data-testid={c.testid} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-[#C4738A]"><c.Icon className="w-[18px] h-[18px]" /></div>
                <div><div className="text-[10px] text-slate-400">{c.label}</div>
                  <div className="font-display text-lg font-bold text-slate-900">{c.val}</div></div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
                    <th className="text-left px-4 py-2.5 font-semibold sticky left-0 bg-slate-50 z-10 min-w-[170px]">Karyawan</th>
                    {Array.from({ length: mx?.days_in_month || 0 }, (_, i) => <th key={i} className="px-0.5 py-2.5 font-semibold text-center min-w-[30px]">{i + 1}</th>)}
                    <th className="px-2 py-2.5 font-semibold text-center">Hadir</th>
                    <th className="px-2 py-2.5 font-semibold text-center">Telat</th>
                    <th className="px-2 py-2.5 font-semibold text-center">Alfa</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {mx === null && <tr><td colSpan={40} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
                  {mx && mx.employees.map((e) => (
                    <tr key={e.id} data-testid={`mx-row-${e.id}`}>
                      <td className="px-4 py-2 sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                            {e.photo ? <img src={e.photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-slate-400">{(e.name || "?")[0]}</div>}
                          </div>
                          <div><div className="text-xs font-semibold text-slate-800 whitespace-nowrap">{e.name}</div>
                            <div className="text-[9px] text-slate-400">{e.employee_id || e.role_label}</div></div>
                        </div>
                      </td>
                      {e.days.map((d, i) => (
                        <td key={i} className="p-0.5 text-center">
                          {d ? (
                            <button data-testid={`mx-cell-${e.id}-${i + 1}`} onClick={() => setSel({ emp: e, d })}
                              className={`w-7 h-7 rounded-md text-[10px] font-bold transition-transform hover:scale-110 ${CELL_CLS[d.letter]} ${sel?.emp?.id === e.id && sel?.d?.date === d.date ? "ring-2 ring-[#7C2D3E] ring-offset-1" : ""}`}>{d.letter}</button>
                          ) : <span className="inline-block w-7 h-7" />}
                        </td>
                      ))}
                      <td className="px-2 py-2 text-center text-xs font-bold text-slate-700">{e.totals.hadir_all}</td>
                      <td className="px-2 py-2 text-center text-xs font-bold text-amber-500">{e.totals.terlambat}</td>
                      <td className="px-2 py-2 text-center text-xs font-bold text-rose-500">{e.totals.belum_absen}</td>
                    </tr>
                  ))}
                  {mx && !mx.employees.length && <tr><td colSpan={40} className="px-4 py-12 text-center text-xs text-slate-400">Tidak ada karyawan untuk filter ini.</td></tr>}
                </tbody>
              </table>
            </div>
            <div data-testid="mx-legend" className="flex items-center gap-4 flex-wrap px-4 py-3 border-t border-slate-50">
              {LEGEND.map(([l, label]) => (
                <span key={l} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[9px] font-bold ${CELL_CLS[l]}`}>{l}</span>{label}
                </span>
              ))}
              <span className="ml-auto text-[10px] text-slate-400">Hadir termasuk terlambat.</span>
            </div>
          </div>

          {sel && sel.d && (
            <div data-testid="mx-detail" className="bg-white rounded-2xl border border-slate-100 p-5">
              <h3 className="font-display font-bold text-sm text-slate-900 mb-4">Detail Absensi • {sel.emp.name} • {formatDateID(sel.d.date)}</h3>
              <div className="flex flex-wrap items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 overflow-hidden flex-shrink-0">
                  {sel.emp.photo ? <img src={sel.emp.photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-lg font-bold text-slate-400">{(sel.emp.name || "?")[0]}</div>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4 flex-1">
                  <div><div className="text-[10px] text-slate-400 mb-0.5">Status</div>
                    <div><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[sel.d.status]}`}>{STATUS_LABEL[sel.d.status]}{sel.d.off_schedule && sel.d.status !== "libur" ? " • Luar Jadwal" : ""}</span></div></div>
                  <div><div className="text-[10px] text-slate-400 mb-0.5">Shift</div><div className="text-xs font-semibold text-slate-800">{sel.d.is_workday ? sel.d.shift : "LIBUR"}</div></div>
                  <div><div className="text-[10px] text-slate-400 mb-0.5">Masuk</div><div className="text-xs font-semibold text-slate-800">{sel.d.clock_in ? formatTimeID(sel.d.clock_in) : "—"}</div></div>
                  <div><div className="text-[10px] text-slate-400 mb-0.5">Pulang</div><div className="text-xs font-semibold text-slate-800">{sel.d.clock_out ? formatTimeID(sel.d.clock_out) : "—"}</div></div>
                  <div><div className="text-[10px] text-slate-400 mb-0.5">Terlambat</div><div className="text-xs font-semibold text-slate-800">{sel.d.late_minutes > 10 ? `${sel.d.late_minutes} menit` : "—"}</div></div>
                  <div><div className="text-[10px] text-slate-400 mb-0.5">Lokasi</div><div className="text-xs font-semibold text-slate-800">{sel.d.location_name ? `${sel.d.location_name} (±${sel.d.distance_m} m)` : "—"}</div></div>
                  <div><div className="text-[10px] text-slate-400 mb-0.5">GPS</div>
                    <div className={`text-xs font-semibold flex items-center gap-1 ${sel.d.in_area === true ? "text-emerald-600" : sel.d.in_area === false ? "text-amber-500" : "text-slate-400"}`}>
                      <MapPin className="w-3 h-3" />{sel.d.in_area === true ? "Dalam radius" : sel.d.in_area === false ? "Luar radius" : "—"}
                    </div></div>
                </div>
                <div className="flex flex-col gap-2 ml-auto">
                  <button data-testid="mx-selfie-btn" onClick={() => setShowSelfie(true)}
                    className="h-9 px-4 rounded-lg bg-[#7C2D3E] text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-[#66222f] transition-colors">
                    <Camera className="w-3.5 h-3.5" /> Lihat Selfie & GPS
                  </button>
                  <button data-testid="mx-correct-btn" onClick={openCorrection}
                    className="h-9 px-4 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1.5 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">
                    <Pencil className="w-3.5 h-3.5" /> Koreksi Absensi
                  </button>
                </div>
              </div>
              {sel.d.corrected && <p className="mt-3 text-[10px] text-amber-600">Absensi ini pernah dikoreksi admin{sel.d.correction_note ? `: ${sel.d.correction_note}` : ""}.</p>}
            </div>
          )}
        </div>
      )}

      {showSelfie && sel?.d && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowSelfie(false)}>
          <div data-testid="mx-selfie-modal" className="bg-white rounded-2xl p-5 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm text-slate-900">Selfie & GPS — {sel.emp.name}</h4>
              <button data-testid="mx-selfie-close" onClick={() => setShowSelfie(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[["Masuk", sel.d.selfie_in], ["Pulang", sel.d.selfie_out]].map(([l, src]) => (
                <div key={l}>
                  <div className="text-[10px] text-slate-400 mb-1">{l}</div>
                  {src ? <img src={src} alt={`Selfie ${l}`} className="rounded-xl w-full aspect-square object-cover bg-slate-100" />
                    : <div className="rounded-xl w-full aspect-square bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-[10px] text-slate-300">Tidak ada selfie</div>}
                </div>
              ))}
            </div>
            {sel.d.lat != null && sel.d.lng != null ? (
              <>
                <div ref={gpsRef} className="h-44 rounded-xl overflow-hidden z-0" />
                <p className="text-[10px] text-slate-400">Koordinat {sel.d.lat.toFixed(5)}, {sel.d.lng.toFixed(5)}{sel.d.location_name ? ` • ${sel.d.location_name} (±${sel.d.distance_m} m)` : ""}</p>
              </>
            ) : <p className="text-[10px] text-slate-400">Tidak ada koordinat GPS tercatat.</p>}
          </div>
        </div>
      )}

      {corr && sel && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCorr(null)}>
          <div data-testid="mx-correct-modal" className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm text-slate-900">Koreksi Absensi</h4>
              <button data-testid="mx-correct-close" onClick={() => setCorr(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-slate-400">{sel.emp.name} • {formatDateID(sel.d.date)}</p>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Status</label>
              <select data-testid="mx-correct-status" value={corr.status} onChange={(e) => setCorr((c) => ({ ...c, status: e.target.value }))}
                className="h-9 w-full px-2.5 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]">
                {[["hadir", "Hadir"], ["terlambat", "Terlambat"], ["sakit", "Sakit"], ["izin", "Izin"], ["cuti", "Cuti"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {(corr.status === "hadir" || corr.status === "terlambat") && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Jam Masuk</label>
                  <input data-testid="mx-correct-in" type="time" value={corr.clock_in} onChange={(e) => setCorr((c) => ({ ...c, clock_in: e.target.value }))}
                    className="h-9 w-full px-2.5 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Jam Pulang</label>
                  <input data-testid="mx-correct-out" type="time" value={corr.clock_out} onChange={(e) => setCorr((c) => ({ ...c, clock_out: e.target.value }))}
                    className="h-9 w-full px-2.5 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]" />
                </div>
              </div>
            )}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Catatan</label>
              <textarea data-testid="mx-correct-note" rows={2} value={corr.note} onChange={(e) => setCorr((c) => ({ ...c, note: e.target.value }))} placeholder="Alasan koreksi..."
                className="w-full px-2.5 py-2 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]" />
            </div>
            <div className="flex gap-2 pt-1">
              <button data-testid="mx-correct-cancel" onClick={() => setCorr(null)} className="flex-1 h-9 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold hover:border-slate-300">Batal</button>
              <button data-testid="mx-correct-save" onClick={submitCorrection} className="flex-1 h-9 rounded-lg bg-[#7C2D3E] text-white text-xs font-semibold hover:bg-[#66222f]">Simpan Koreksi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
